/**
 * Client-minted, immutable conversation id.
 *
 * The id is minted ONCE when a conversation starts and never changes for its
 * lifetime; only Start Over clears it (the next send mints a fresh one). It is
 * written to localStorage synchronously BEFORE the first request goes out, so
 * two turns that race before any response returns still carry the SAME id and
 * converge on one server row instead of forking into two sessions.
 *
 * Signed-out visitors mint one too: their turns stay keyed to it in telemetry,
 * and the database row is created on their first authenticated turn, which is
 * what preserves the conversation across login.
 */
export const AGENT_SESSION_ID_STORAGE_KEY = "agent_session_id";

/**
 * Durable record that the customer deliberately ended their conversation.
 *
 * Start Over clears the id, but "no id" is also what a brand-new browser looks
 * like — and the server-side restore fallback answers that with the customer's
 * most recent conversation. Without a durable marker the reset was undone on
 * the very next render (and again on every later page load), handing back the
 * conversation they had just ended. The marker is cleared the moment this
 * browser owns a conversation id again.
 */
export const AGENT_SESSION_RESET_STORAGE_KEY = "agent_session_reset";

/**
 * Whether the stored id belongs to a SIGNED-IN customer.
 *
 * An anonymous visitor's minted id must survive sign-in (that continuity is the
 * whole point of client-minted ids), but a signed-in customer's id must not
 * outlive their session on a shared browser — the next anonymous visitor would
 * otherwise send turns stamped with somebody else's conversation.
 */
export const AGENT_SESSION_OWNER_STORAGE_KEY = "agent_session_owner";
const AUTHENTICATED_OWNER = "authenticated";

/**
 * Strict UUIDv4 — mirrors the server's request-boundary check so a value the
 * API would reject with a 400 is never sent (or restored) in the first place.
 */
const UUID_V4_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isAgentSessionId(value: unknown): value is string {
  return typeof value === "string" && UUID_V4_PATTERN.test(value);
}

/** Stored id, or null when absent/unusable/unreadable. */
export function readStoredAgentSessionId(): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const stored = localStorage.getItem(AGENT_SESSION_ID_STORAGE_KEY);
    return isAgentSessionId(stored) ? stored : null;
  } catch {
    return null;
  }
}

export function storeAgentSessionId(sessionId: string): void {
  if (typeof window === "undefined" || !isAgentSessionId(sessionId)) {
    return;
  }
  try {
    localStorage.setItem(AGENT_SESSION_ID_STORAGE_KEY, sessionId);
    // This browser owns a conversation again, so the reset it recorded is
    // spent: a later load may restore normally.
    localStorage.removeItem(AGENT_SESSION_RESET_STORAGE_KEY);
  } catch {
    // Private-mode storage can throw; the in-memory store id still governs
    // this tab's conversation.
  }
}

export function clearStoredAgentSessionId(): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    localStorage.removeItem(AGENT_SESSION_ID_STORAGE_KEY);
    localStorage.removeItem(AGENT_SESSION_OWNER_STORAGE_KEY);
  } catch {
    // Nothing to do — a stale id is re-validated on read.
  }
}

/**
 * Clears an unspent fresh-start intent at an account ownership boundary.
 * Keep this separate from clearStoredAgentSessionId: markAgentSessionReset
 * deliberately clears the id before writing the marker for the same identity.
 */
export function clearAgentSessionReset(): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    localStorage.removeItem(AGENT_SESSION_RESET_STORAGE_KEY);
  } catch {
    // Best effort; storage may be unavailable in private browsing contexts.
  }
}

/**
 * Records an intentional Start Over: the id is dropped AND the reset is
 * remembered, so the server-side restore fallback stays out of the way until
 * this browser starts a new conversation.
 */
export function markAgentSessionReset(): void {
  clearStoredAgentSessionId();
  if (typeof window === "undefined") {
    return;
  }
  try {
    localStorage.setItem(AGENT_SESSION_RESET_STORAGE_KEY, "1");
  } catch {
    // Best effort: without the marker the fallback may restore, which is the
    // pre-existing behaviour rather than a new failure.
  }
}

export function wasAgentSessionReset(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    return localStorage.getItem(AGENT_SESSION_RESET_STORAGE_KEY) !== null;
  } catch {
    return false;
  }
}

/** Marks the stored id as belonging to a signed-in customer. */
export function markStoredAgentSessionAuthenticated(): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    if (localStorage.getItem(AGENT_SESSION_ID_STORAGE_KEY) === null) {
      return;
    }
    localStorage.setItem(AGENT_SESSION_OWNER_STORAGE_KEY, AUTHENTICATED_OWNER);
  } catch {
    // Best effort; see storeAgentSessionId.
  }
}

export function isStoredAgentSessionAuthenticated(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    return (
      localStorage.getItem(AGENT_SESSION_OWNER_STORAGE_KEY) ===
      AUTHENTICATED_OWNER
    );
  } catch {
    return false;
  }
}

/**
 * The id this conversation owns, minting one on first use.
 *
 * Resolution order is deliberate: the live store first (this tab's
 * conversation), then localStorage (a reload of the same conversation), then a
 * fresh mint. The result is persisted before returning so the caller can send
 * it immediately.
 */
export function getOrCreateAgentSessionId(
  currentSessionId: string | null | undefined
): string | null {
  if (isAgentSessionId(currentSessionId)) {
    storeAgentSessionId(currentSessionId);
    return currentSessionId;
  }

  const stored = readStoredAgentSessionId();
  if (stored) {
    return stored;
  }

  if (
    typeof window === "undefined" ||
    typeof crypto?.randomUUID !== "function"
  ) {
    return null;
  }

  const minted = crypto.randomUUID();
  storeAgentSessionId(minted);
  return minted;
}
